/**
 * @module services/liquidation-submission
 * @description UC-REG-169 — Cliente HTTP para el flujo B de liquidación
 * (submissions del prestador). Aislado del flujo viejo en
 * services/liquidation.js.
 */
import api from "./api";

const endpoint = "/liquidation-submissions";

export default {
  /**
   * @description Listado paginado con filtros.
   * @param {Object} params - { provider, year, month, status, offset, limit }
   * @returns {Promise<{docs, total, offset, limit}>}
   */
  list: (params = {}) => api.get(endpoint, { params }).then((r) => r.data),

  /**
   * @description Detalle completo del submission (para polling).
   * @param {string} id
   */
  getById: (id) => api.get(`${endpoint}/${id}`).then((r) => r.data),

  /**
   * @description Crea un submission — dispara procesamiento async.
   * @param {Object} body - { provider, billingPeriod, sourceMode, invoiceData?, lines, notes? }
   * @returns {Promise<{submissionId, status, contentHash, linesTotal}>}
   */
  create: (body) => api.post(endpoint, body).then((r) => r.data),

  /**
   * @description Anula el submission (revert lógico auditable).
   * @param {string} id
   * @param {string} reason - Obligatorio
   */
  revert: (id, reason) =>
    api.post(`${endpoint}/${id}/revert`, { reason }).then((r) => r.data),

  /**
   * @description Re-corre el pipeline sobre las líneas del submission.
   * @param {string} id
   */
  reprocess: (id) =>
    api.post(`${endpoint}/${id}/reprocess`).then((r) => r.data),

  /**
   * @description UC-REG-196 — Confirma el submission en 'reviewing' →
   *   materializa SRs para líneas aceptadas + pasa a 'resolved'.
   * @param {string} id
   */
  confirm: (id) => api.post(`${endpoint}/${id}/confirm`).then((r) => r.data),

  /**
   * @description UC-REG-210 — Lista TRXs candidatas para match manual de
   *   una línea (mismo prestador, mismo día).
   * @param {string} submissionId
   * @param {string} lineId
   * @returns {Promise<{candidates: Array}>}
   */
  matchCandidates: (submissionId, lineId) =>
    api
      .get(`${endpoint}/${submissionId}/lines/${lineId}/match-candidates`)
      .then((r) => r.data),

  /**
   * @description UC-REG-210 — Vincula una línea con una TRX existente.
   * @param {string} submissionId
   * @param {string} lineId
   * @param {string} serviceRecordId
   */
  manualMatch: (submissionId, lineId, serviceRecordId) =>
    api
      .patch(`${endpoint}/${submissionId}/lines/${lineId}/manual-match`, {
        serviceRecordId,
      })
      .then((r) => r.data),

  /**
   * @description Override manual del auditor sobre una línea.
   * @param {string} submissionId
   * @param {string} lineId
   * @param {Object} body - { decision: 'accept'|'reject'|'debit', debitAmount?, reason }
   */
  overrideLine: (submissionId, lineId, body) =>
    api
      .patch(`${endpoint}/${submissionId}/lines/${lineId}`, body)
      .then((r) => r.data),

  /**
   * @description UC-REG-171 iter 3 — sube archivo (Excel/CSV) al preview
   *   del importer. Devuelve headers + sampleRows + mapping detectado +
   *   preview transformado + fingerprint (para guardar template después).
   * @param {File} file
   * @param {Object} [opts]
   * @param {string} [opts.provider] - ObjectId (para lookup del template)
   * @param {string} [opts.sheetName]
   * @returns {Promise<Object>}
   */
  previewImport(file, opts = {}) {
    const form = new FormData();
    form.append("file", file);
    if (opts.provider) form.append("provider", opts.provider);
    if (opts.sheetName) form.append("sheetName", opts.sheetName);
    return api
      .post(`${endpoint}/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description UC-REG-171 iter 2 — guarda template de mapping para
   *   (provider, fingerprint) + aprende sinónimos en shared dictionary.
   * @param {Object} body - { provider, fingerprint, headers, columnMapping, fileTypeHint?, sheetName?, headerRowIndex? }
   */
  saveTemplate: (body) =>
    api.post(`${endpoint}/templates`, body).then((r) => r.data),

  /**
   * @description UC-REG-171 iter 4 — corre IA sobre el preview parseado.
   * @param {Object} body - { headers, sampleRows, existingMapping, unmappedColumns }
   */
  resolveWithAI: (body) =>
    api.post(`${endpoint}/preview/ai`, body).then((r) => r.data),

  // ─── UC-REG-180/181/182 Fases 2/3/4 — Factura + NC + Pago ──────────

  /**
   * @description Devuelve la factura del submission + URLs firmadas.
   * @param {string} submissionId
   * @returns {Promise<{invoice: Object|null}>}
   */
  getInvoice: (submissionId) =>
    api.get(`${endpoint}/${submissionId}/invoice`).then((r) => r.data),

  /**
   * @description Sube o reemplaza la factura del prestador. Multipart.
   * @param {string} submissionId
   * @param {Object} data - { invoiceNumber, invoiceDate, cuit?, razonSocial?, netAmount, taxAmount?, totalAmount }
   * @param {File} [file] - PDF de la factura (opcional)
   */
  saveInvoice(submissionId, data, file) {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null && v !== "") form.append(k, v);
    });
    if (file) form.append("file", file);
    return api
      .post(`${endpoint}/${submissionId}/invoice`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Elimina la factura + attachments. Vuelve SR a 'liquidated'.
   * @param {string} submissionId
   */
  deleteInvoice: (submissionId) =>
    api.delete(`${endpoint}/${submissionId}/invoice`).then((r) => r.data),

  /**
   * @description Agrega una nota de crédito a la factura. Multipart.
   * @param {string} submissionId
   * @param {Object} data - { ncNumber, amount, ncDate?, reason? }
   * @param {File} [file]
   */
  addCreditNote(submissionId, data, file) {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null && v !== "") form.append(k, v);
    });
    if (file) form.append("file", file);
    return api
      .post(`${endpoint}/${submissionId}/invoice/credit-notes`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Elimina una NC + recalcula matchStatus.
   * @param {string} submissionId
   * @param {string} ncId
   */
  deleteCreditNote: (submissionId, ncId) =>
    api
      .delete(`${endpoint}/${submissionId}/invoice/credit-notes/${ncId}`)
      .then((r) => r.data),

  /**
   * @description Marca la factura como pagada + registra comprobante.
   *   Requiere factura con matchStatus='ok'. Multipart.
   * @param {string} submissionId
   * @param {Object} data - { paidAt, amount, paymentMethod?, bankRef?, notes? }
   * @param {File} [file]
   */
  savePayment(submissionId, data, file) {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null && v !== "") form.append(k, v);
    });
    if (file) form.append("file", file);
    return api
      .post(`${endpoint}/${submissionId}/payment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Deshace el pago. Vuelve SR a 'invoiced'.
   * @param {string} submissionId
   */
  deletePayment: (submissionId) =>
    api.delete(`${endpoint}/${submissionId}/payment`).then((r) => r.data),
};
